/**
 * ConfirmDialog Component (ORCH-336)
 *
 * Purpose: A plain "are you sure?" dialog: a title naming what will happen, a
 *          short explanation, Cancel and one confirm button. For reversible or
 *          routine actions (deactivate, remove from a tenant, leave without
 *          saving). Destructive deletes that should make the person type the
 *          item's name use DeleteConfirmationDialog instead. Cancel is a plain
 *          text button beside the filled confirm (the theme's button hierarchy,
 *          ORCH-424) in the shared DialogFooter; callers don't restyle the footer.
 * Dependencies: MUI Dialog, LoadingButton, DialogFooter
 */

import React from 'react';
import { Dialog, DialogContent, DialogTitle } from '@mui/material';
import LoadingButton from './LoadingButton';
import { SecondaryButton } from './ActionButtons';
import DialogFooter from './DialogFooter';

export interface ConfirmDialogProps {
  open: boolean;
  title: string;
  /** What happens, in a sentence or two. */
  children: React.ReactNode;
  confirmLabel: string;
  cancelLabel?: string;
  /** Colour of the confirm button; `error` for an action that removes something. */
  confirmColor?: 'primary' | 'error' | 'warning';
  loading?: boolean;
  onConfirm: () => void;
  onClose: () => void;
  dataTestId?: string;
}

const ConfirmDialog: React.FC<ConfirmDialogProps> = ({
  open,
  title,
  children,
  confirmLabel,
  cancelLabel = 'Cancel',
  confirmColor = 'primary',
  loading = false,
  onConfirm,
  onClose,
  dataTestId = 'confirm-dialog',
}) => (
  <Dialog
    open={open}
    onClose={loading ? undefined : onClose}
    maxWidth="xs"
    fullWidth
    data-testid={dataTestId}
  >
    <DialogTitle>{title}</DialogTitle>
    <DialogContent>{children}</DialogContent>
    <DialogFooter>
      <SecondaryButton onClick={onClose} disabled={loading} data-testid={`${dataTestId}-cancel`}>
        {cancelLabel}
      </SecondaryButton>
      <LoadingButton
        variant="contained"
        color={confirmColor}
        loading={loading}
        onClick={onConfirm}
        data-testid={`${dataTestId}-confirm`}
      >
        {confirmLabel}
      </LoadingButton>
    </DialogFooter>
  </Dialog>
);

export default ConfirmDialog;
